import React from 'react';
import * as Icons from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { useFilter } from '../../context/FilterContext';
import { scrollToAnchor } from './Navigation';
import { LogIn, UserPlus, User as UserIcon, X, LogOut } from 'lucide-react';

/**
 * Customer-facing mobile menu. Slides in from the left on mobile.
 * Tapping any sidebar item:
 *   1. sets the category filter (so the page below filters)
 *   2. scrolls to the linked anchor when provided
 *   3. AUTO-CLOSES the menu so the user can see the result
 */
export default function MobileMenu({ open, onOpenChange, onLogin, onRegister }) {
  const { data } = useSite();
  const { user, logout } = useAuth();
  const { category, setCategory, setQuery } = useFilter();

  // Note: we intentionally do NOT toggle `body { overflow: hidden }` here.
  // The backdrop element (z-index above content) already blocks pointer events
  // on the page, so the user can't accidentally interact with content behind
  // the drawer. Locking body overflow would freeze the page if, for any
  // reason, the drawer is unmounted before its cleanup useEffect runs — that
  // edge-case has been observed in production after clicking a menu item.

  const handleItem = (item) => {
    if (item.id === 'getid' || item.id === 'get-id' || item.label?.toLowerCase() === 'get id') {
      setCategory('home');
      setQuery('');
      setTimeout(() => {
        const el = document.getElementById('get-id') || document.getElementById('inplay') || document.getElementById('exchanges');
        if (el) {
          const offset = window.innerWidth < 768 ? 140 : 110;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        }
      }, 50);
      onOpenChange(false);
      return;
    }
    if (item.id) { setCategory(item.id); setQuery(''); }
    if (item.link) {
      if (/^https?:/i.test(item.link)) { window.open(item.link, '_blank'); }
      else if (item.link === '/' || item.link === '') { window.scrollTo({ top: 0, behavior: 'smooth' }); }
      else { scrollToAnchor(item.link); }
    }
    // Always close the drawer AFTER state has committed
    setTimeout(() => onOpenChange(false), 0);
  };

  const branding = data.branding || {};
  const items = data.sidebar || [];

  return (
    <div
      className={`lg:hidden fixed inset-0 z-[70] ${open ? '' : 'pointer-events-none'}`}
      data-testid="site-mobile-menu"
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        onClick={() => onOpenChange(false)}
        className={`absolute inset-0 bg-black transition-opacity duration-200 ${open ? 'opacity-60' : 'opacity-0'}`}
      />
      {/* Drawer panel */}
      <div
        className={`absolute inset-y-0 left-0 w-[85%] sm:w-[360px] max-w-[400px] bg-zinc-950 border-r border-zinc-800 shadow-2xl transition-transform duration-200 ease-out flex flex-col ${open ? 'translate-x-0' : '-translate-x-full'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-4 flex items-center justify-between border-b border-zinc-800 shrink-0" style={{ background: branding.headerBg || '#777544' }}>
          <div className="flex items-baseline gap-1">
            {branding.logoImage ? (
              <img src={branding.logoImage} alt={branding.logoText} className="h-7 w-auto object-contain" />
            ) : (
              <>
                <span className="font-display text-2xl font-extrabold tracking-[0.12em] text-black/85">{branding.logoText}</span>
                <span className="font-display text-[10px] font-bold text-black/70">{branding.logoAccent}</span>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-black/85 hover:text-black active:scale-95"
            style={{ touchAction: 'manipulation' }}
            data-testid="site-mobile-menu-close-btn"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 p-3 border-b border-zinc-900 shrink-0">
          {user ? (
            <>
              <div className="col-span-2 h-10 rounded-md bg-zinc-900 border border-zinc-800 px-3 flex items-center gap-2 text-sm">
                <UserIcon size={14} className="text-[color:var(--gold)]" />
                <span className="text-zinc-100 font-semibold">{user.name}</span>
                <span className="text-zinc-500 text-xs ml-auto">+91 {user.mobile}</span>
              </div>
              <button
                type="button"
                onClick={() => { logout(); onOpenChange(false); }}
                className="col-span-2 h-10 rounded-md bg-red-600/15 border border-red-900/40 text-red-300 font-bold text-sm flex items-center justify-center gap-1"
                style={{ touchAction: 'manipulation' }}
                data-testid="site-mobile-menu-logout"
              >
                <LogOut size={14} /> Logout
              </button>
            </>
          ) : (
            <>
              {(data.header?.showLogin !== false) && (
              <button
                type="button"
                onClick={() => { onLogin?.(); onOpenChange(false); }}
                className="h-10 rounded-md bg-[color:var(--gold)] text-black font-bold text-sm flex items-center justify-center gap-1"
                style={{ touchAction: 'manipulation' }}
                data-testid="site-mobile-menu-login"
              >
                <LogIn size={14} />Login
              </button>
              )}
              {(data.header?.showSignup !== false) && (
              <button
                type="button"
                onClick={() => { onRegister?.(); onOpenChange(false); }}
                className="h-10 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 font-bold text-sm flex items-center justify-center gap-1"
                style={{ touchAction: 'manipulation' }}
                data-testid="site-mobile-menu-signup"
              >
                <UserPlus size={14} />Sign Up
              </button>
              )}
            </>
          )}
        </div>

        <div className="py-2 flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="site-mobile-menu-items">
          {items.map((item) => {
            const Icon = Icons[item.icon] || Icons.Circle;
            const isActive = category === item.id;
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => handleItem(item)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm font-semibold transition-colors active:scale-[0.99] ${isActive ? 'text-[color:var(--gold)] bg-[color:var(--gold)]/10' : 'text-zinc-200 hover:bg-zinc-900'}`}
                style={{ touchAction: 'manipulation' }}
                data-testid={`site-mobile-menu-item-${item.id}`}
              >
                <Icon size={16} className={isActive ? 'text-[color:var(--gold)]' : 'text-zinc-400'} />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
          {items.length === 0 && (
            <div className="text-zinc-500 text-sm px-5 py-6 text-center">No menu items configured.</div>
          )}
        </div>
      </div>
    </div>
  );
}
